import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import { get_api, post_api, put_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format, time_format } from '../../../../constants';
import { fmt_amount, status_badge } from './helpers';

// OQ-6 evidence field set (text + file). File fields hold a Stripe file id from the upload endpoint.
const EVIDENCE_TEXT = [
  ['product_description', 'Product description'],
  ['customer_name', 'Customer name'],
  ['customer_email_address', 'Customer email'],
  ['billing_address', 'Billing address'],
  ['access_activity_log', 'Access / activity log'],
  ['cancellation_policy_disclosure', 'Cancellation policy disclosure'],
  ['uncategorized_text', 'Other (uncategorized)']
];
const EVIDENCE_FILES = [
  ['receipt', 'Receipt'],
  ['service_documentation', 'Service documentation'],
  ['customer_communication', 'Customer communication'],
  ['cancellation_policy', 'Cancellation policy']
];
const INTERNAL_STATUS_OPTIONS = [
  'NEW',
  'REVIEWING',
  'EVIDENCE_SUBMITTED',
  'ACCEPTED',
  'WONT_CONTEST',
  'CLOSED'
];

const input_style = {
  width: '100%',
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  border: '1px solid var(--input-border)',
  borderRadius: '4px',
  padding: '4px 6px'
};

class ChargebackDetails extends Component {
  state = {
    loading: true,
    item: null,
    history: [],
    evidence: {},
    notes: '',
    internal_status: '',
    accept_armed: false,
    saving: false,
    msg: ''
  };

  componentDidMount() {
    this.load_data();
  }

  get_id = () => new URLSearchParams(this.props.location.search).get('id') || '';

  load_data = () => {
    const id = this.get_id();
    if (!id) {
      this.setState({ loading: false });
      return;
    }
    this.setState({ loading: true }, () => {
      const base = `/v2/cms_user/chargebacks/${encodeURIComponent(id)}`;
      const detail_url = get_api_url(base, false, true);
      const history_url = get_api_url(`${base}/history`, false, true);
      Promise.all([get_api(detail_url, true), get_api(history_url, true)])
        .then(([d, h]) => {
          // get_api resolves the raw axios response → the goserver payload is at res.data.data.
          const item = (d && d.data && d.data.data) || null;
          const hist = (h && h.data && h.data.data) || [];
          this.setState({
            loading: false,
            item,
            history: Array.isArray(hist) ? hist : [],
            // prefill the evidence text fields we already know (OQ-6 auto-prefill).
            evidence: item
              ? {
                  product_description: 'MyYogaTeacher membership',
                  customer_name: item.student_name || '',
                  customer_email_address: item.student_email || ''
                }
              : {},
            notes: (item && item.internal_notes) || '',
            internal_status: (item && item.internal_status) || ''
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  fmt_dt = (v) =>
    v ? moment(v).tz(this.props.iana_timezone).format(`${day_format} ${time_format}`) : '--';

  is_super_user = () => this.props.access_type === 'SUPER_USER';

  // err_msg surfaces the goserver reason from a rejected axios error.
  err_msg = (err, fallback) => {
    const reason =
      err &&
      err.response &&
      err.response.data &&
      (err.response.data.reason || err.response.data.message);
    return reason ? `${fallback}: ${reason}` : fallback;
  };

  set_evidence_field = (field, value) =>
    this.setState((prev) => ({ evidence: { ...prev.evidence, [field]: value } }));

  upload_file = (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('field', field);
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/evidence/upload`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    post_api(url, fd, true, 'formdata')
      .then((res) => {
        const payload = (res && res.data && res.data.data) || {};
        this.set_evidence_field(field, payload.file_id || '');
        this.setState({ saving: false, msg: `Uploaded ${field}` });
      })
      .catch((err) =>
        this.setState({ saving: false, msg: this.err_msg(err, `Upload failed for ${field}`) })
      );
  };

  submit_evidence = () => {
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/evidence/submit`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    post_api(url, this.state.evidence, true)
      .then(() =>
        this.setState({ saving: false, msg: 'Evidence submitted to Stripe' }, this.load_data)
      )
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Submit failed') }));
  };

  accept_dispute = () => {
    if (!this.state.accept_armed) {
      this.setState({
        accept_armed: true,
        msg: 'Click Accept again to confirm conceding this chargeback.'
      });
      return;
    }
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/accept`,
      false,
      true
    );
    this.setState({ saving: true, msg: '', accept_armed: false });
    post_api(url, {}, true)
      .then(() =>
        this.setState({ saving: false, msg: 'Dispute accepted (conceded)' }, this.load_data)
      )
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Accept failed') }));
  };

  save_notes = () => {
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/notes`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    put_api(
      url,
      { internal_notes: this.state.notes, internal_status: this.state.internal_status },
      true
    )
      .then(() => this.setState({ saving: false, msg: 'Notes saved' }, this.load_data))
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Save failed') }));
  };

  field = (label, value) => (
    <div
      className="flex"
      style={{ padding: '4px 0', borderBottom: '1px solid var(--border-default)' }}
    >
      <div style={{ width: '180px', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ flex: 1, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );

  render_detail = (item) => {
    const badge = status_badge(item.status);
    return (
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          padding: '16px',
          maxWidth: '760px'
        }}
      >
        {this.field(
          'Kind',
          item.kind === 'early_fraud_warning'
            ? 'Early Fraud Warning (near chargeback)'
            : 'Chargeback'
        )}
        {this.field(
          'Status',
          <span style={{ ...badge, padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
            {item.status || '--'}
          </span>
        )}
        {item.match_status && item.match_status !== 'matched'
          ? this.field(
              'Match',
              <span style={{ color: 'var(--state-warning-text)' }}>
                ⚠ {item.match_status} — needs manual investigation
              </span>
            )
          : null}
        {this.field('Amount', fmt_amount(item.amount_minor, item.currency))}
        {this.field('Reason', item.reason || '--')}
        {this.field(
          'Student',
          item.student_uuid ? (
            <a
              className="link-no-dec"
              href={`/studentprofile?id=${item.student_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand-primary)' }}
            >
              {item.student_name || item.student_uuid}
              {item.student_email ? ` · ${item.student_email}` : ''}
            </a>
          ) : (
            '--'
          )
        )}
        {this.field('Charge', item.charge_id || '--')}
        {this.field('Payment Intent', item.payment_intent_id || '--')}
        {this.field('Evidence due by', this.fmt_dt(item.evidence_due_by))}
        {this.field('Internal status', item.internal_status || '--')}
        {this.field('Handled by', item.handled_by || '--')}
        {this.field('Created (Stripe)', this.fmt_dt(item.stripe_created_date))}
      </div>
    );
  };

  render_lifecycle = (item) => {
    // Lifecycle is SUPER_USER-only and applies to formal disputes (EFWs have no Stripe lifecycle).
    if (!this.is_super_user() || item.kind !== 'dispute') return null;
    const { evidence, saving, msg, notes, internal_status, accept_armed } = this.state;
    return (
      <div style={{ marginTop: '20px', maxWidth: '760px' }}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Actions (SUPER_USER)
        </div>
        {msg ? (
          <div
            className="ct-vsm-font"
            style={{ color: 'var(--state-info-text)', marginBottom: '8px' }}
          >
            {msg}
          </div>
        ) : null}

        <div style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '8px 0 4px' }}>
          Evidence
        </div>
        {EVIDENCE_TEXT.map(([key, label]) => (
          <div key={key} style={{ marginBottom: '6px' }}>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {label}
            </div>
            <textarea
              rows={key === 'access_activity_log' || key === 'uncategorized_text' ? 2 : 1}
              style={input_style}
              value={evidence[key] || ''}
              onChange={(e) => this.set_evidence_field(key, e.target.value)}
            />
          </div>
        ))}
        {EVIDENCE_FILES.map(([key, label]) => (
          <div key={key} style={{ marginBottom: '6px' }}>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {label}
              {evidence[key] ? (
                <span style={{ color: 'var(--state-success-text)' }}>
                  {' '}
                  · uploaded ({evidence[key]})
                </span>
              ) : null}
            </div>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => this.upload_file(key, e.target.files && e.target.files[0])}
            />
          </div>
        ))}
        <button
          type="button"
          className="ct-small-btn"
          disabled={saving}
          onClick={this.submit_evidence}
          style={{
            marginTop: '6px',
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)'
          }}
        >
          Submit evidence
        </button>
        <button
          type="button"
          className="ct-small-btn"
          disabled={saving}
          onClick={this.accept_dispute}
          style={{
            marginTop: '6px',
            marginLeft: '8px',
            background: accept_armed ? 'var(--state-error-bg)' : 'var(--surface-card)',
            color: accept_armed ? 'var(--state-error-text)' : 'var(--text-primary)',
            border: '1px solid var(--border-default)'
          }}
        >
          {accept_armed ? 'Confirm accept (concede)' : 'Accept (concede)'}
        </button>

        <div style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '14px 0 4px' }}>
          Notes / status
        </div>
        <textarea
          rows={3}
          style={input_style}
          value={notes}
          onChange={(e) => this.setState({ notes: e.target.value })}
          placeholder="Internal notes"
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
          <select
            style={{ ...input_style, width: '220px' }}
            value={internal_status}
            onChange={(e) => this.setState({ internal_status: e.target.value })}
          >
            <option value="">(internal status)</option>
            {INTERNAL_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ct-small-btn"
            disabled={saving}
            onClick={this.save_notes}
          >
            Save notes
          </button>
        </div>
      </div>
    );
  };

  render_history = (history) => (
    <div style={{ marginTop: '20px', maxWidth: '760px' }}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
        Audit history
      </div>
      {history.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No actions recorded.</div>
      ) : (
        history.map((h) => (
          <div
            key={h.id}
            style={{
              padding: '8px 0',
              borderBottom: '1px solid var(--border-default)',
              color: 'var(--text-primary)'
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {h.action}
              {h.from_status || h.to_status ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {' '}
                  {h.from_status || '—'} → {h.to_status || '—'}
                </span>
              ) : null}
            </div>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {h.actor_type}
              {h.cms_user_name ? ` · ${h.cms_user_name}` : ''} · {this.fmt_dt(h.created_date)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  render() {
    const { loading, item, history } = this.state;
    return (
      <CommonHeader loading={loading} title="Chargeback details">
        {!loading && !item ? (
          <div style={{ color: 'var(--text-muted)' }}>Chargeback not found.</div>
        ) : null}
        {item ? (
          <>
            {this.render_detail(item)}
            {this.render_lifecycle(item)}
            {this.render_history(history)}
          </>
        ) : null}
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone,
  access_type: state.home.access_type
});

export default connect(mapStateToProps)(ChargebackDetails);
